import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { LabelCounter } from 'app/components/Common/LabelCounter'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Card, Props as CardProps } from 'app/components/ServicesPreview/Card'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import React, { memo } from 'react'

export interface Props {
  cards?: CardProps[]
  counter?: string
  description?: string
  label?: string
  title?: string
}

export const ServicesPreview = memo(function ServicesPreview({
  cards,
  counter,
  description,
  label,
  title,
}: Props) {
  if (!cards) {
    return null
  }

  const cardsCopy = [...cards]

  const leftList = cardsCopy.splice(0, Math.round(cards.length / 2))
  const rightList = cardsCopy

  return (
    <Container>
      <Top>
        {label ? (
          <FadeInUp delay={0.4}>
            <LabelCounter label={label} counter={counter} variant="light" />
          </FadeInUp>
        ) : null}
        {title ? <Title length={40} tag="h3" text={title} /> : null}
        <Media lessThan="desktopSmall">
          {description ? (
            <FadeInUp delay={0.6}>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}
        </Media>
      </Top>
      <Wrapper row dial={1} space="between" wrap>
        <LeftSide>
          {leftList.map((item: any, index) => (
            <FadeInUp delay={index * 0.1} key={index}>
              <Card {...item} />
            </FadeInUp>
          ))}
        </LeftSide>

        <RightSide>
          <Media greaterThanOrEqual="desktopSmall">
            {description ? (
              <FadeInUp delay={0.6}>
                <Description
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </FadeInUp>
            ) : null}
          </Media>
          {rightList.map((item: any, index) => (
            <FadeInUp delay={index * 0.1} key={index}>
              <Card {...item} />
            </FadeInUp>
          ))}
        </RightSide>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  padding: 6.5rem 10vw 0;
  position: relative;
  margin-bottom: 3rem;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colors.variants.primaryDark};
    height: 77.778vh;
    width: 100%;
  }

  @media (max-width: 1439px) {
    &:before {
      height: 100vh;
    }
  }

  @media (max-width: 1279px) {
    &:before {
      height: 120vh;
    }
  }

  @media (max-width: 1199px) {
    padding: 3rem 1.5rem 0 1.5rem;
    margin-bottom: 1.5rem;

    &:before {
      height: 65vh;
    }
  }
`

const Wrapper = styled(FlexBox)`
  gap: 3.75rem;

  @media (max-width: 1199px) {
    gap: 0;
  }
`

const Top = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  margin-bottom: 4.5rem;

  @media (max-width: 1199px) {
    margin-bottom: 2.25rem;
  }
`

const LeftSide = styled.div`
  overflow: hidden;
  z-index: 1;
  row-gap: 4.5rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 1199px) {
    width: 100%;
    row-gap: 1.5rem;
  }

  @media (min-width: 1200px) {
    > div {
      &:last-of-type {
        margin-bottom: 0;
        display: flex;
        justify-content: flex-end;
      }
      &:first-of-type {
        a > div {
          width: 36.75vw;
        }
      }
    }
  }
`

const RightSide = styled.div`
  z-index: 1;
  row-gap: 4.5rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 1199px) {
    width: 100%;
    row-gap: 1.5rem;
    margin-top: 1.5rem;
  }

  @media (min-width: 1200px) {
    > div {
      &:last-of-type {
        margin-bottom: 0;

        a > div {
          width: 36.75vw;
        }
      }
    }
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.xl}

  color: ${({ theme }) => theme.colors.variants.neutralLight3};
  margin-bottom: 0.75rem;

  @media (max-width: 1199px) {
    ${styles.title.large}

    color: ${({ theme }) => theme.colors.variants.neutralLight3};
  }
`

const Description = styled.div`
  ${styles.description.light}

  padding: 0 2.778vw;

  max-width: 30.556vw;

  @media (max-width: 1199px) {
    max-width: none;
    padding: 0;
    margin-top: 2.25rem;
    margin-bottom: 1rem;
  }
`
